import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Undo2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { importAPI, errText } from '../lib/importAPI';
import { MappingStep } from '../components/clients/ImportMapping';
import { PreviewStep } from '../components/clients/ImportPreview';

const History = ({ rows, onRollback }) => (
  <Card data-testid="import-history">
    <CardHeader><CardTitle className="text-base">Import history</CardTitle></CardHeader>
    <CardContent className="space-y-1 text-xs">
      {!rows.length && <p className="text-muted-foreground">No imports yet.</p>}
      {rows.map((b) => (
        <div key={b.id} className="flex flex-wrap items-center gap-2 border-b py-1" data-testid={`import-batch-${b.id}`}>
          <span className="font-medium">{b.ref}</span><span>{b.filename}</span><span className="text-muted-foreground">{b.created_at?.slice(0, 16).replace('T', ' ')}</span>
          <span className="capitalize">{b.status.replace('_', ' ')}</span>
          {b.counts && <span className="text-muted-foreground">created {b.counts.created} · updated {b.counts.updated}</span>}
          <span className="font-mono text-muted-foreground" title={b.file_hash}>#{b.file_hash.slice(0, 10)}</span>
          {b.status === 'applied' && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onRollback(b)} data-testid={`import-rollback-${b.id}`}><Undo2 className="w-3 h-3 mr-1" />Roll back</Button>}
        </div>
      ))}
    </CardContent>
  </Card>
);

export default function ClientImport() {
  const navigate = useNavigate();
  const [up, setUp] = useState(null);
  const [columns, setColumns] = useState({});
  const [entityValues, setEntityValues] = useState({});
  const [pv, setPv] = useState(null);
  const [updates, setUpdates] = useState({});
  const [amb, setAmb] = useState({});
  const [skipInvalid, setSkipInvalid] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [hist, setHist] = useState([]);
  const [opts, setOpts] = useState({ split_names: false, blank_entity: null });
  const load = () => { importAPI.history().then(setHist).catch(() => {}); importAPI.mappings().then(setMappings).catch(() => {}); };
  useEffect(load, []);
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); } };
  const loadMapping = (m) => { setColumns(m.columns); setEntityValues(m.entity_values || {}); if (m.options && Object.keys(m.options).length) setOpts({ ...opts, ...m.options }); };
  const upload = (f) => f && run(async () => {
    if (up) importAPI.discard(up.id).catch(() => {});
    const r = await importAPI.upload(f); setUp(r); setColumns(r.suggested_mapping); setPv(null);
    if (r.preset) { setEntityValues(r.preset.entity_values || {}); setOpts(r.preset.options); }
    if (r.previously_applied) toast.warning('This exact file was imported before – identical rows will be skipped');
  });
  const discard = () => run(async () => { await importAPI.discard(up.id); setUp(null); setPv(null); load(); });
  const body = () => ({ columns, entity_values: entityValues, skip_invalid: skipInvalid, updates, ambiguous: amb, ...opts });
  const preview = () => run(async () => setPv(await importAPI.preview(up.id, body())));
  const apply = () => run(async () => { const r = await importAPI.apply(up.id, body()); if (r.status === 'no_changes') toast.info('No changes – everything in this file was already imported'); else toast.success(`Imported: ${r.counts.created} created, ${r.counts.updated} updated`); setUp(null); setPv(null); load(); });
  const rollback = async (b) => {
    if (!window.confirm(`Roll back ${b.ref}? New clients with no activity since are deleted and updated fields restored.`)) return;
    const done = (r) => { toast.success(`Rolled back: ${r.deleted} deleted, ${r.restored} restored${r.kept.length ? `, ${r.kept.length} kept` : ''}${r.cascaded?.length ? ` (also ${r.cascaded.join(', ')})` : ''}`); load(); };
    setBusy(true);
    try {
      done(await importAPI.rollback(b.id));
    } catch (e) {
      const x = e?.response?.data?.detail;
      if (x?.code === 'LATER_IMPORT_DEPENDS') {
        if (window.confirm(`${x.message}\n\nLater imports: ${x.later.map((l) => l.ref).join(', ')}\n\nRoll back all of them, newest first?`)) {
          try { done(await importAPI.rollback(b.id, true)); } catch (e2) { toast.error(errText(e2)); }
        }
      } else toast.error(errText(e));
    } finally { setBusy(false); }
  };
  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="client-import-page">
      <Button variant="ghost" className="-ml-2" onClick={() => navigate('/clients')}><ArrowLeft className="w-4 h-4 mr-2" />Clients</Button>
      <Card>
        <CardHeader>
          <CardTitle>Import clients (CSV)</CardTitle>
          <CardDescription>Xero Practice Manager client list export. Nothing is saved until you apply; matches use ABN, TFN, email or client code only – never name alone.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <label className="inline-flex items-center gap-2">
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ''; }} data-testid="import-file" />
            <span className="inline-flex h-9 items-center rounded-md border px-3 text-sm cursor-pointer hover:bg-muted/50"><Upload className="w-4 h-4 mr-2" />{up ? 'Upload a different file' : 'Choose CSV file'}</span>
            <span className="text-xs text-muted-foreground">UTF-8 or UTF-8 with BOM, comma or semicolon, up to 5,000 rows / 10 MB</span>
          </label>
          {up && <MappingStep up={up} columns={columns} setColumns={setColumns} mappings={mappings} opts={opts} setOpts={setOpts} onLoadMapping={loadMapping}
            onSaveMapping={(name) => run(async () => { await importAPI.saveMapping({ name, columns, entity_values: entityValues, options: opts }); toast.success('Mapping saved'); load(); })} />}
          {up && (
            <div className="flex items-center gap-2">
              <Button onClick={preview} disabled={busy || !columns.display_name && !columns.first_name} data-testid="import-preview-btn">Preview import</Button>
              <Button variant="ghost" onClick={discard} disabled={busy} data-testid="import-discard-btn">Discard upload</Button>
            </div>
          )}
          {pv && <PreviewStep pv={pv} updates={updates} setUpdates={setUpdates} amb={amb} setAmb={setAmb} entityValues={entityValues} setEntityValues={setEntityValues} />}
          {pv && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={skipInvalid} onChange={(e) => setSkipInvalid(e.target.checked)} data-testid="import-skip-invalid" />Skip invalid rows</label>
              <Button variant="outline" onClick={preview} disabled={busy} data-testid="import-refresh-preview">Refresh preview</Button>
              <Button onClick={apply} disabled={busy} data-testid="import-apply-btn">Apply import</Button>
            </div>
          )}
        </CardContent>
      </Card>
      <History rows={hist} onRollback={rollback} />
    </div>
  );
}
